/**
 * Database Attachment Scanner for Export Size Dialog
 *
 * sl-ht07.5: Scans database blocks in docs for attachment/image blobs
 * and groups them by database with size information.
 */

import type { Store } from '@blocksuite/store';
import {
  collectDatabaseRowBlobIds,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  databaseCellKeysOfType,
} from '@streamlain/database-identity';
import { Map as YMap } from 'yjs';

export interface DatabaseAttachmentInfo {
  databaseId: string;
  databaseTitle: string;
  blobIds: Set<string>;
  fileCount: number;
  totalSize: number; // in bytes, populated externally after scanning
}

type DatabaseBlobColumns = {
  attachmentCellKeys: Set<string>;
  imageCellKeys: Set<string>;
};

type DatabaseAttachmentAccumulator = DatabaseAttachmentInfo &
  DatabaseBlobColumns;

const SERVER_DATABASE_ROW_SHARD_DOC_ID =
  /^db\$[^$]+\$database\$([^$]+)\$rows\$[^$]+$/;

/**
 * Scans all database blocks in the given docs for attachment/image blobs.
 * Returns per-database info with blob IDs (sizes must be resolved separately).
 */
export function scanDatabaseAttachments(
  stores: Store[]
): DatabaseAttachmentInfo[] {
  const databases = new Map<string, DatabaseAttachmentAccumulator>();

  for (const store of stores) {
    const root = store.root;
    if (!root) continue;

    walkBlocks(root, block => {
      if (block.flavour !== 'affine:database') return;

      const columns = block.props.columns as unknown[] | undefined;
      if (!columns) return;

      const databaseIdValue = block.props.databaseId;
      const databaseId =
        typeof databaseIdValue === 'string' && databaseIdValue
          ? databaseIdValue
          : block.id;
      const info =
        databases.get(databaseId) ??
        createDatabaseAttachmentAccumulator(databaseId, block.props.title);
      databases.set(databaseId, info);

      // A type-changed column keeps earlier cells for a change back.
      for (const key of databaseCellKeysOfType(columns, 'attachment'))
        info.attachmentCellKeys.add(key);
      for (const key of databaseCellKeysOfType(columns, 'image'))
        info.imageCellKeys.add(key);
    });
  }

  // Cell values live in the row-shard documents; the database block above
  // holds the columns and the title.
  for (const store of stores) {
    const databaseId = parseDatabaseRowShardDatabaseId(store.id);
    if (!databaseId) continue;

    const info = databases.get(databaseId);
    if (!info) continue;

    store.spaceDoc
      .getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)
      .forEach(row =>
        collectDatabaseRowBlobIds(
          info.blobIds,
          row instanceof YMap ? row.toJSON() : row,
          info
        )
      );
  }

  return [...databases.values()]
    .filter(info => info.blobIds.size > 0)
    .map(({ attachmentCellKeys: _, imageCellKeys: __, ...info }) => ({
      ...info,
      fileCount: info.blobIds.size,
    }));
}

function createDatabaseAttachmentAccumulator(
  databaseId: string,
  rawTitle: unknown
): DatabaseAttachmentAccumulator {
  const title =
    typeof rawTitle === 'string'
      ? rawTitle
      : rawTitle &&
          typeof (rawTitle as { toString?: () => string }).toString ===
            'function'
        ? (rawTitle as { toString: () => string }).toString()
        : '';

  return {
    attachmentCellKeys: new Set(),
    blobIds: new Set(),
    databaseId,
    databaseTitle: title || 'Untitled Database',
    fileCount: 0,
    imageCellKeys: new Set(),
    totalSize: 0,
  };
}

function parseDatabaseRowShardDatabaseId(docId: string): string | null {
  return SERVER_DATABASE_ROW_SHARD_DOC_ID.exec(docId)?.[1] ?? null;
}

/**
 * Resolves blob sizes for scanned database info.
 * Uses workspace blob storage to look up each blob's size.
 */
export async function resolveBlobSizes(
  infos: DatabaseAttachmentInfo[],
  getBlobSize: (blobId: string) => Promise<number>
): Promise<void> {
  for (const info of infos) {
    let total = 0;
    for (const blobId of info.blobIds) {
      total += await getBlobSize(blobId);
    }
    info.totalSize = total;
  }
}

/**
 * Collects all blob IDs from excluded (unchecked) databases.
 */
export function collectExcludedBlobIds(
  infos: DatabaseAttachmentInfo[],
  excludedDatabaseIds: Set<string>
): Set<string> {
  const excluded = new Set<string>();
  for (const info of infos) {
    if (excludedDatabaseIds.has(info.databaseId)) {
      for (const blobId of info.blobIds) {
        excluded.add(blobId);
      }
    }
  }
  return excluded;
}

// ─── Internal ────────────────────────────────────────────────────────────────

interface BlockLike {
  id: string;
  flavour: string;
  props: Record<string, unknown>;
  children: BlockLike[];
}

function walkBlocks(block: BlockLike, fn: (b: BlockLike) => void): void {
  fn(block);
  for (const child of block.children) {
    walkBlocks(child, fn);
  }
}
